import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NavGuardProvider } from './contexts/NavGuardContext';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import EstimatesList from './pages/EstimatesList';
import EstimateEditor from './pages/EstimateEditor';
import TemplatesPage from './pages/TemplatesPage';
import ChangeOrdersList from './pages/ChangeOrdersList';
import ChangeOrderEditor from './pages/ChangeOrderEditor';
import InvoicesList from './pages/InvoicesList';
import InvoiceEditor from './pages/InvoiceEditor';
import JobCostsList from './pages/JobCostsList';
import JobCostDetail from './pages/JobCostDetail';
import PriceBook from './pages/PriceBook';
import ConcreteCalculator from './pages/calculators/ConcreteCalculator';
import DrywallCalculator from './pages/calculators/DrywallCalculator';
import PaintingCalculator from './pages/calculators/PaintingCalculator';
import ToolsLibrary from './pages/ToolsLibrary';
import TasksPage from './pages/TasksPage';
import SchedulePage from './pages/SchedulePage';
import ClientsDirectory from './pages/ClientsDirectory';
import ClientDetail from './pages/ClientDetail';
import DashboardLayout from './pages/DashboardLayout';
import GmailCallback from './pages/GmailCallback';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F6F5]">
        <div className="w-8 h-8 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <DashboardLayout>{children}</DashboardLayout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F6F5]">
        <div className="w-8 h-8 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id - detect BEFORE ProtectedRoute runs
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/estimates" element={<ProtectedRoute><EstimatesList /></ProtectedRoute>} />
      <Route path="/estimates/:id" element={<ProtectedRoute><EstimateEditor /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
      <Route path="/change-orders" element={<ProtectedRoute><ChangeOrdersList /></ProtectedRoute>} />
      <Route path="/change-orders/:id" element={<ProtectedRoute><ChangeOrderEditor /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><InvoicesList /></ProtectedRoute>} />
      <Route path="/invoices/:id" element={<ProtectedRoute><InvoiceEditor /></ProtectedRoute>} />
      <Route path="/job-costs" element={<ProtectedRoute><JobCostsList /></ProtectedRoute>} />
      <Route path="/job-costs/:id" element={<ProtectedRoute><JobCostDetail /></ProtectedRoute>} />
      <Route path="/price-book" element={<ProtectedRoute><PriceBook /></ProtectedRoute>} />
      <Route path="/price-book/concrete" element={<ProtectedRoute><ConcreteCalculator /></ProtectedRoute>} />
      <Route path="/price-book/drywall" element={<ProtectedRoute><DrywallCalculator /></ProtectedRoute>} />
      <Route path="/price-book/painting" element={<ProtectedRoute><PaintingCalculator /></ProtectedRoute>} />
      <Route path="/tools" element={<ProtectedRoute><ToolsLibrary /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
      <Route path="/schedule" element={<ProtectedRoute><SchedulePage /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><ClientsDirectory /></ProtectedRoute>} />
      <Route path="/clients/:clientKey" element={<ProtectedRoute><ClientDetail /></ProtectedRoute>} />
      <Route path="/gmail-callback" element={<ProtectedRoute><GmailCallback /></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NavGuardProvider>
          <AppRouter />
          <Toaster position="bottom-right" richColors />
        </NavGuardProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
